import { Turbo } from "@hotwired/turbo-rails"
import { resyncTagifyFieldsFromHiddenInRoot } from "../helpers/customized_tagify.js"

/**
 * Custom Turbo Stream action for handling successful autosave
 *
 * This action is triggered by the server after a form auto-save.
 * It dispatches the autosave:success event and updates field components
 * without reloading the whole form. Tagify fields are re-synced from hidden
 * inputs (may reset caret) so `{{ … }}` renders as tags.
 *
 * Pass reload_frame: true to replace the whole Turbo Frame with fresh HTML
 * from frame_url (needed when options depend on saved values, e.g. account type).
 * This bypasses data-turbo-permanent so the UI matches what was saved.
 *
 * Usage in controller:
 *   render turbo_stream: turbo_stream.autosave_success(
 *     frame_url: edit_step_settings_path(@step),
 *     frame_id: "settings_#{ActionView::RecordIdentifier.dom_id(@step)}"
 *   )
 */
Turbo.StreamActions.autosave_success = function () {
  // Read frame info from stream element attributes (set by server)
  const frameUrl = this.getAttribute('frame_url')
  const frameId = this.getAttribute('frame_id')
  const reloadFrame = this.getAttribute('reload_frame') === 'true'

  dispatchAutosaveSuccessEvent()

  if (reloadFrame) {
    reloadInputFrame(frameUrl, frameId)
  } else {
    updateFieldComponents(frameUrl, frameId)
  }
}

/**
 * Simple version that only dispatches autosave:success event
 * Use this when you only need the save indicator, not component updates
 *
 * Usage in controller:
 *   render turbo_stream: turbo_stream.autosave_notify
 */
Turbo.StreamActions.autosave_notify = function () {
  dispatchAutosaveSuccessEvent()
}

function dispatchAutosaveSuccessEvent() {
  const event = new CustomEvent("autosave:success", {
    bubbles: true,
    detail: { source: "turbo-stream-action" }
  })
  document.dispatchEvent(event)
  window.dispatchEvent(event)
}

async function fetchFrameDocument(frameUrl, frameId) {
  const response = await fetch(frameUrl, {
    headers: {
      'Accept': 'text/html',
      'Turbo-Frame': frameId
    }
  })

  if (!response.ok) return null

  const html = await response.text()
  const parser = new DOMParser()
  return parser.parseFromString(html, 'text/html')
}

async function reloadInputFrame(frameUrl, frameId) {
  try {
    if (!frameUrl || !frameId) return

    const frameEl = document.getElementById(frameId)
    if (!frameEl) return

    const doc = await fetchFrameDocument(frameUrl, frameId)
    if (!doc) return

    const newFrame = doc.getElementById(frameId)
    if (!newFrame) return

    // replaceWith uses the server HTML as-is, so turbo-permanent fields
    // from the previous form cannot keep a stale selected value.
    frameEl.replaceWith(newFrame)
  } catch (error) {
    // Silently ignore errors
  }
}

async function updateFieldComponents(frameUrl, frameId) {
  try {
    if (!frameUrl || !frameId) return

    const doc = await fetchFrameDocument(frameUrl, frameId)
    if (!doc) return

    // Update all field_changed_indicator components
    doc.querySelectorAll('[id^="field_changed_indicator_"]').forEach(newEl => {
      const existingEl = document.getElementById(newEl.id)
      if (existingEl && existingEl.innerHTML !== newEl.innerHTML) {
        existingEl.innerHTML = newEl.innerHTML
      }
    })

    // Update all field_reset_section components
    doc.querySelectorAll('[id^="field_reset_section_"]').forEach(newEl => {
      const existingEl = document.getElementById(newEl.id)
      if (existingEl && existingEl.innerHTML !== newEl.innerHTML) {
        existingEl.innerHTML = newEl.innerHTML
      }
    })

    const frameEl = document.getElementById(frameId)
    if (frameEl) {
      resyncTagifyFieldsFromHiddenInRoot(frameEl)
      // Lazy frames only fetch once; reload after save when the panel is open so we
      // do not hit the network for every autosave while <details> stay collapsed.
      frameEl
        .querySelectorAll(
          'turbo-frame[src*="raw_data"], turbo-frame[src*="dev_input_template"]'
        )
        .forEach((tf) => {
          const details = tf.closest('details')
          if (details && !details.open) return
          if (typeof tf.reload === 'function') tf.reload()
        })
    }
  } catch (error) {
    // Silently ignore errors
  }
}
